/* eslint-disable no-async-promise-executor */
import { Input, FormControl, FormLabel, Grid, GridItem, Flex, Box, Center } from "@chakra-ui/react";
import React, { memo, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { CalendarPriceSearchItem, DeletePriceSearchIcon, EditPriceSearchIcon, YellowAlert } from "~misc/icons";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { brFloatFormat } from "~services/utils/formatNumber";
import Info from "~components/Form/components/Info";
import { capitalizeWords } from "~services/utils/formatText";
import { db } from "~/db/db";

import CurrencyInputAlternateMM from "~components/Form/components/CurrencyInputAlternateMM";
import { CloseIcon } from "@chakra-ui/icons";

import { checkTodaySearchs, formatDateToCustomString } from "../utils/searchHelpers";
import useAlertIconVisible from "../hooks/useAlertIconVisible";

const PriceSearchItem = memo(
  ({
    pesquisa,
    handleDelete,
    trilhaCliente,
    setModalSettings,
    setModalResponseSettings,
    lastSearchDate,
    updateHook,
    diasMostrarDataAtualizacao,
    buttonClickClear,
  }) => {
    const { parameter: idPesquisa } = useParams();
    const [searchParams] = useSearchParams();
    const [markup, setMarkup] = useState(0);
    const [volume, setVolume] = useState("");
    const [sellIn, setSellIn] = useState(0);
    const [sellOut, setSellOut] = useState(0);
    const [precoFOB, setPrecoFOB] = useState(0);
    const [precoCIF, setPrecoCIF] = useState(0);
    const [unidadeMedida, setUnidadeMedida] = useState({});
    const [hasDataUpdated, setHasDataUpdated] = useState(0);
    const incoterms = pesquisa?.dadosGerais?.incoterms;
    const [showOverlay, setShowOverlay] = useState(null);
    const [toIgnoreBlur, setToIgnoreBlur] = useState({
      volume: false,
    });

    const [initialValue, setInitialValue] = useState({});
    const [previousBlurValue, setPreviousBlurValue] = useState({
      sellIn: "",
      sellOut: "",
      precoFOB: "",
      precoCIF: "",
      volume: "",
    });

    const volumeRef = useRef(null);
    const sellOutRef = useRef(null);
    const sellInRef = useRef(null);
    const precoFobRef = useRef(null);
    const precoCifRef = useRef(null);

    const getFloatValue = (value, setState = null) => {
      let replacedValue = brFloatFormat((String(value) || "").replace("R$", ""));
      if (setState != null && typeof setState == "function") setState(replacedValue);
      replacedValue = parseFloat(replacedValue.replace(".", "").replace(",", ".").replace("R$", ""));

      return replacedValue;
    };

    const { showAlertIcon } = useAlertIconVisible(pesquisa, lastSearchDate);

    const [disableAllInputs, setDisableAllInputs] = useState(false);

    const handleChangeGeneric = async ({ event, key, setState, checkSearchs = false }) => {
      const isVolume = key === "volume";
      const rawValue = event.target?.value ?? event;
      let replacedValue = isVolume ? 
        (rawValue === null || rawValue === "" || isNaN(rawValue) ? null : parseInt(rawValue || 0))  : 
        getFloatValue(rawValue, setState);

      if(isNaN(replacedValue)) replacedValue = "";

      if (checkSearchs && initialValue[key] !== replacedValue) {
        await checkTodaySearchs({
          clienteId: pesquisa.clienteId,
          pesquisaId: pesquisa.id,
          obj: {
            [key]: replacedValue,
          },
        });
      }

      if (isVolume) {
        setState(typeof replacedValue == 'string' ? replacedValue.trim().replace(/\s+/g, " ") : replacedValue)
      };
    };

    const setToIgnore = ({ fieldName, value = false }) => {
      setToIgnoreBlur((prev) => ({
        ...prev,
        [fieldName]: value,
      }));
    };

    const onBlurSellIn = (event) => {
      const value = getFloatValue(event.target.value);

      if (showOverlay?.sellIn && (value === 0 || !value)) {
        return;
      }

      if (value >= getFloatValue(sellOut) && value > 0) {
        setDisableAllInputs(true);

        setModalResponseSettings({
          isOpen: true,
          message: "Valor de Sell In deve ser menor que Sell Out",
          closeCallback: () => {
            setDisableAllInputs(false);
            sellInRef?.current?.focus();
          },
        });

        return;
      }

      const onConfirm = () => {
        if (value == 0) {
          setShowOverlay({
            ...showOverlay,
            sellIn: true,
          });
        }

        handleChangeGeneric({
          event,
          key: "sellIn",
          setState: setSellIn,
          checkSearchs: true,
        }).then(() => {
          checarAtualizacao({ value, field: "sellIn" });
        });
      };

      if (!value || value == 0) {
        showConfirmZeroValuePopup("sellIn", "Sell In", value, onConfirm, sellInRef);
        return;
      }

      onConfirm();
    };

    const onBlurSellOut = async (event) => {
      const value = getFloatValue(event.target.value);

      if (getFloatValue(sellIn) >= value && getFloatValue(sellIn) > 0) {
        setDisableAllInputs(true);

        setModalResponseSettings({
          isOpen: true,
          message: "Valor de Sell In deve ser menor que Sell Out",
          closeCallback: () => {
            setDisableAllInputs(false);
            sellOutRef?.current?.focus();
          },
        });

        return;
      }

      if (value <= 0) {
        setDisableAllInputs(true);

        setModalResponseSettings({
          isOpen: true,
          message: "O valor de Sell Out é obrigatório",
          closeCallback: () => {
            setDisableAllInputs(false);
            sellOutRef?.current?.focus();
          },
        });

        return;
      }

      await handleChangeGeneric({
        event,
        key: "sellOut",
        setState: setSellOut,
        checkSearchs: true,
      }).then(() => {
        checarAtualizacao({ value, field: "sellOut" });
      });
    };

    const onBlurPrecoCIF = (event) => {
      const value = getFloatValue(event.target.value);

      const onConfirm = () => {
        if (value == 0) {
          setShowOverlay({
            ...showOverlay,
            precoCIF: true,
          });
        }

        handleChangeGeneric({
          event,
          key: "precoCIF",
          setState: setPrecoCIF,
          checkSearchs: true,
        }).then(() => {
          checarAtualizacao({ value, field: "precoCIF" });
        });
      };

      if (value < getFloatValue(precoFOB) && value > 0 && getFloatValue(precoFOB) > 0) {
        setDisableAllInputs(true);

        setModalResponseSettings({
          isOpen: true,
          message: "O valor de Preço FOB não pode ser maior do que o valor de Preço CIF",
          closeCallback: () => {
            setDisableAllInputs(false);

            precoCifRef?.current?.focus();
          },
        });

        return true;
      }

      if (!value || value == 0) {
        showConfirmZeroValuePopup("precoCIF", "Preço CIF", value, onConfirm, precoCifRef);
        return;
      }

      onConfirm();
    };

    const onBlurPrecoFOB = (event) => {
      const value = getFloatValue(event.target.value);
      const onConfirm = () => {
        if (value > getFloatValue(precoCIF) && value > 0 && getFloatValue(precoCIF) > 0) {
          setDisableAllInputs(true);

          setModalResponseSettings({
            isOpen: true,
            message: "O valor de Preço FOB não pode ser maior do que o valor de Preço CIF",
            closeCallback: () => {
              setDisableAllInputs(false);

              precoFobRef?.current?.focus();
            },
          });

          precoFobRef?.current?.focus();
          return;
        }

        if (value == 0) {
          setShowOverlay({
            ...showOverlay,
            precoFOB: true,
          });
        }

        handleChangeGeneric({
          event,
          key: "precoFOB",
          setState: setPrecoFOB,
          checkSearchs: true,
        }).then(() => {
          checarAtualizacao({ value, field: "precoFOB" });
        });
      };

      if (!value || value == 0) {
        showConfirmZeroValuePopup("precoFOB", "Peço FOB", value, onConfirm, precoFobRef);
        return;
      }

      onConfirm();
    };

    const onConfirmVolume = ({ value, event }) => {
      value = isNaN(value) ? null : value;
      if (value == 0 || value == null) {
        setShowOverlay({
          ...showOverlay,
          volume: true,
        });
      }

      handleChangeGeneric({ event, key: "volume", setState: setVolume, checkSearchs: true }).then(() => {
        checarAtualizacao({ value, field: "volume" });
        if (volumeRef?.current) {
          volumeRef.current.blur();
        }
      });
    };

    const onBlurVolume = (event) => {
      const value = parseInt(event?.target?.value || event);

      if (!value || value == 0 || isNaN(value)) {

        setModalSettings({
          isOpen: true,
          field: 'Volume',
          sub: value === 0 ? 
          "Ao preencher o volume com 0, a pesquisa será atualizada para 'Sem consumo no mês'. Deseja confirmar?" : //SEM CONSUMO MENSAL
          "Ao não adicionar valor ao volume, a pesquisa será atualizada para 'Sem Informação'. Deseja confirmar?", //SEM INFORMAÇÃO - MANTÉM O TEXTO ORIGINAL DO MODAL
          handleConfirm: () => {

            onConfirmVolume({ value, event: value });
            setToIgnore({ fieldName: 'volume', value: true });
          },
          handleCancel: () => {
            volumeRef?.current?.focus();
          },
        });

        return;
      }

      onConfirmVolume({ value: isNaN(value) ? null : value, event });
    };

    const showConfirmZeroValuePopup = (fieldName, field, value, handleConfirm, inputRef = null) => {
      if (value <= 0) {
        setDisableAllInputs(true);

        setModalSettings({
          isOpen: true,
          field,
          handleConfirm: () => {
            setDisableAllInputs(false);

            if (fieldName == "volume") {
              handleConfirm?.({ value, event: value });
              setToIgnore({ fieldName, value: true });
              return;
            }

            return handleConfirm?.();
          },
          handleCancel: () => {
            setDisableAllInputs(false);

            if (inputRef?.current) inputRef.current.focus();
          },
        });
      }
    };

    const ultimaAtualizacaoEmDias = (date) => {
      const now = new Date();
      const updatedAt = new Date(date);

      const diffByMilliseconds = now - updatedAt;

      return Math.floor(diffByMilliseconds / (1000 * 60 * 60 * 24));
    };

    const createComparisonObject = () => ({
      sellOut: { temp: getFloatValue(sellOut), original: initialValue.sellOut },
      sellIn: { temp: getFloatValue(sellIn), original: initialValue.sellIn },
      volume: { temp: parseInt(volume), original: initialValue.volume },
      precoCIF: { temp: getFloatValue(precoCIF), original: initialValue.precoCIF },
      precoFOB: { temp: getFloatValue(precoFOB), original: initialValue.precoFOB },
    });

    const areAllValuesOriginal = (comparisonObj) => {
      const result = Object.entries(comparisonObj).reduce((acc, [key, values]) => {
        acc[key] = values.temp === values.original;
        return acc;
      }, {});
      return Object.values(result).every((match) => match);
    };

    const buildUpdateForValueChange = (field, value, key) => {
      const setPreviousValue = (value = "") => {
        setPreviousBlurValue((previous) => ({
          ...previous,
          [field]: value,
        }));
      };

      setPreviousValue(key);
      return {
        [field]: value,
        updated: true,
        ultimaAtualizacao: formatDateToCustomString(new Date()),
        ultimaAtualizacaoEN: new Date(),
      };
    };

    const buildUpdateForRestoration = (field, value) => {
      const setPreviousValue = () => {
        setPreviousBlurValue((previous) => ({
          ...previous,
          [field]: "",
        }));
      };

      setPreviousValue();
      const original = initialValue.originalUltimaAtualizacao
        ? new Date(initialValue.originalUltimaAtualizacao)
        : new Date();

      return {
        [field]: value,
        ultimaAtualizacao: formatDateToCustomString(original),
        ultimaAtualizacaoEN: original,
      };
    };

    const checarAtualizacao = async ({ value, field }) => {
      try {
        if (!initialValue || Object.keys(initialValue).length === 0) {
          return null;
        }

        const key = `${value} - ${initialValue[field]}`;
        const isNewOrChanged = previousBlurValue[field] === "" || previousBlurValue[field] !== key;
        const hasValueChanged = value !== initialValue[field] && previousBlurValue[field] !== key;

        let toUpdate = { [field]: value };
        let updated = isNewOrChanged;

        if (hasValueChanged) {
          toUpdate = buildUpdateForValueChange(field, value, key);
        } else if (updated) {
          const comparisonObj = createComparisonObject();
          const allValuesMatch = areAllValuesOriginal(comparisonObj);

          if (allValuesMatch) {
            toUpdate = buildUpdateForRestoration(field, value);
          }
        }

        if (updated) {
          await db.mapaDaMina.update(pesquisa.id, toUpdate);
          setHasDataUpdated((prev) => prev + 1);
        }

        return updated;
      } catch (error) {
        console.error(error);
        return false;
      }
    };

    useEffect(() => {
      const volumeValue = pesquisa?.infosAdicionais?.volume;

      setVolume(typeof volumeValue === "number" && !isNaN(volumeValue)
          ? volumeValue.toFixed(0)
          : null
      );
      setSellIn(brFloatFormat(pesquisa.infosAdicionais.sellIn.toFixed(2)));
      setSellOut(brFloatFormat(pesquisa.infosAdicionais.sellOut.toFixed(2)));
      setPrecoCIF(brFloatFormat(pesquisa.infosAdicionais.precoCIF.toFixed(2)));
      setPrecoFOB(brFloatFormat(pesquisa.infosAdicionais.precoFOB.toFixed(2)));
      setUnidadeMedida(pesquisa?.infosAdicionais?.medida);

      (async () => {
        const dbValue = await db.mapaDaMina.where("id").equals(pesquisa.id).first();
        if (dbValue) {
          const id = dbValue.originalId ?? dbValue.id;
          if (id) {
            const dbTempValue = await db.mapaDaMinaOriginal.where("id").equals(id).first();
            setInitialValue({
              ...(dbTempValue || dbValue),
              originalUltimaAtualizacao: dbValue.originalUltimaAtualizacao ?? dbValue.ultimaAtualizacao ?? null,
            });
          }
        }
      })();

      setShowOverlay({
        sellIn: pesquisa.infosAdicionais.sellIn == 0,
        volume: pesquisa.infosAdicionais.volume === null || pesquisa.infosAdicionais.volume === 0,
        precoFOB: pesquisa.infosAdicionais.precoFOB == 0,
        precoCIF: pesquisa.infosAdicionais.precoCIF == 0,
      });
    }, []);

    useEffect(() => {
      if (initialValue && typeof initialValue == "object" && Object.entries(initialValue)?.length > 0) {
        setPreviousBlurValue({
          sellIn: `${initialValue.sellIn} - ${initialValue.sellIn}`,
          sellOut: `${initialValue.sellOut} - ${initialValue.sellOut}`,
          precoFOB: `${initialValue.precoFOB} - ${initialValue.precoFOB}`,
          precoCIF: `${initialValue.precoCIF} - ${initialValue.precoCIF}`,
          volume: `${initialValue.volume} - ${initialValue.volume}`,
        });
      }
    }, [initialValue]);

    useEffect(() => {
      if (hasDataUpdated != 0) {
        updateHook();
      }
    }, [hasDataUpdated]);

    useEffect(() => {
      if (buttonClickClear !== null && initialValue && Object.keys(initialValue).length > 0) {
        setSellIn(initialValue.sellIn || 0);
        setSellOut(initialValue.sellOut || 0);
        setVolume(initialValue.volume);
        setPrecoFOB(initialValue.precoFOB || 0);
        setPrecoCIF(initialValue.precoCIF || 0);
        setUnidadeMedida(initialValue.medida || {});

        setShowOverlay({
          sellIn: (initialValue.sellIn || 0) == 0,
          volume: initialValue.volume === null || initialValue.volume === 0,
          precoFOB: (initialValue.precoFOB || 0) == 0,
          precoCIF: (initialValue.precoCIF || 0) == 0,
        });

        setHasDataUpdated((prev) => prev + 1);
      }
    }, [buttonClickClear, initialValue, pesquisa.id]);

    useEffect(() => {
      const sellInClean = typeof sellIn == "string" ? String(sellIn).replace(".", "").replace(",", ".") : sellIn;
      const sellOutClean = String(sellOut).replace(".", "").replace(",", ".");
      if (sellInClean > 0) {
        setMarkup(((sellOutClean / sellInClean - 1) * 100).toFixed(2));
      }
    }, [sellIn, sellOut]);

    const dadosGeraisPesquisa = pesquisa?.dadosGerais;

    const getIncotermsDisplayValue = () => {
      if (!incoterms) return "";
      return incoterms === 1 ? "FOB" : "CIF";
    };

    return (
      <div className="p-4 first:mt-0 mt-4 rounded-md relative">
        <span
          className={`${
            dadosGeraisPesquisa?.player?.nome?.toLowerCase() === "votorantim" ? "bg-[#4472C5]" : "bg-[#ED7E30]"
          } h-[90%] w-2 absolute rounded-full -left-1 top-1/2 -translate-y-1/2 z-10`}
        />
        <span
          className={`${
            dadosGeraisPesquisa?.player?.nome?.toLowerCase().includes("votorantim") ? "bg-[#E2F0FF]" : "bg-[#FCF1F1]"
          } absolute left-0 right-0 top-0 bottom-0 z-20 rounded-md`}
        />
        <div className="relative z-30">
          <div className="priceSearchItem__container flex items-center justify-between border-b pb-4 mb-4 flex-col-reverse md:flex-row gap-y-4 md:gap-y-0">
            <div className="priceSearchItem__card-title">
              <div className="flex">
                <p className="font-bold text-xl text-primaria flex items-center gap-x-4 uppercase">
                  {dadosGeraisPesquisa?.grupoProduto?.nome}-{dadosGeraisPesquisa?.produto?.nome}-
                  {dadosGeraisPesquisa?.embalagemCapacidade.nome}
                  {showAlertIcon && <YellowAlert />}
                </p>
              </div>
              {ultimaAtualizacaoEmDias(pesquisa.ultimaAtualizacaoEN) >= diasMostrarDataAtualizacao && (
                <p className="text-primaria flex items-center gap-x-2">
                  <CalendarPriceSearchItem />
                  Última atualização: {pesquisa?.ultimaAtualizacao}
                </p>
              )}
            </div>
            <div>
              <Box flex="0 0 auto" px="5">
                <Flex direction={["column", "row"]} align="stretch" h="100%" justifyContent="center">
                  <Center
                    as="button"
                    type="button"
                    h="50%"
                    alignItems="center"
                    onClick={() => handleDelete(pesquisa.id)}
                  >
                    <div className="scale-75">
                      <DeletePriceSearchIcon />
                    </div>
                  </Center>
                  <Center as="button" h="50%" alignItems="center" mr={2}>
                    <Link
                      to={`/pesquisa-de-preco/${idPesquisa}/editar/${pesquisa.id}${
                        searchParams.get("cameFromVisitaView") ? "?cameFromVisitaView=true" : ""
                      }`}
                    >
                      <div className="scale-75">
                        <EditPriceSearchIcon />
                      </div>
                    </Link>
                  </Center>
                </Flex>
              </Box>
            </div>
            <div className="">
              <p className="font-bold text-xl text-primaria text-end gap-x-4 uppercase priceSearchItem__card-company">
                {dadosGeraisPesquisa?.player?.nome}
              </p>
            </div>
          </div>
          <Flex align="stretch" direction={["column", "row"]} className="flex-col md:flex-row gap-y-6 md:gap-y-0">
            <Box flex="1 0 auto">
              <Grid
                templateColumns="repeat(auto-fill, minmax(200px, 1fr))"
                gap={6}
                className="!flex !flex-col md:!grid items-end"
              >
                <GridItem>
                  <FormControl>
                    <FormLabel color="brand.dark">Modal (Incoterms)</FormLabel>
                    <Input
                      bg="white"
                      className="cursor-not-allowed bg-gray-300"
                      defaultValue={getIncotermsDisplayValue()}
                      disabled
                    />
                  </FormControl>
                </GridItem>
                <GridItem>
                  <FormControl>
                    <FormLabel color="brand.dark">Canal</FormLabel>
                    <Input
                      bg="white"
                      className="cursor-not-allowed bg-gray-300"
                      defaultValue={dadosGeraisPesquisa?.canal?.nome}
                      disabled
                    />
                  </FormControl>
                </GridItem>
                {trilhaCliente === "IND" && (
                  <GridItem>
                    <FormLabel color="brand.dark">Origem (Centro)</FormLabel>
                    <Input
                      bg="white"
                      className="cursor-not-allowed bg-gray-300"
                      defaultValue={dadosGeraisPesquisa?.pesquisaCentroOrigem?.nome}
                      disabled
                    />
                  </GridItem>
                )}
                <GridItem>
                  <FormControl>
                    <FormLabel color="brand.dark">
                      Volume (Mês) {unidadeMedida?.nome ? `- ${capitalizeWords(unidadeMedida.nome)}` : ""}
                    </FormLabel>
                    <div style={{ position: "relative" }}>
                      <Input
                        bg="white"
                        value={volume}
                        onChange={(event) => handleChangeGeneric({ event, key: "volume", setState: setVolume })}
                        onBlur={(e) => {
                          const isTabNavigation = e.relatedTarget !== null;

                          if (!toIgnoreBlur.volume || isTabNavigation) {
                            onBlurVolume(e);
                          } else {
                            setToIgnore({ fieldName: "volume", value: false });
                          }
                        }}
                        className="bg-gray-300"
                        disabled={disableAllInputs}
                        ref={volumeRef}
                        paddingRight="2rem"
                      />
                      {volume ? (
                        <button
                          type="button"
                          onClick={() => {
                            volumeRef?.current?.focus();
                            setVolume('');
                          }}
                          style={{
                            position: "absolute",
                            right: "0.5rem",
                            top: "50%",
                            transform: "translateY(-50%)",
                            background: "transparent",
                            border: "none",
                            cursor: "pointer",
                          }}
                        >
                          <CloseIcon color="gray.500" />
                        </button>
                      ) : null}

                      {showOverlay?.volume && (
                        <InputNoInformationOverlay
                          setShowOverlay={setShowOverlay}
                          showOverlay={showOverlay}
                          name="volume"
                          inputRef={volumeRef}
                          inputText={volume == null ? "Sem Informação" : (volume == 0 ? "Sem consumo no mês" : "")}
                        />
                      )}
                    </div>
                  </FormControl>
                </GridItem>

                {trilhaCliente == "TAC" && (
                  <>
                    <FormControl>
                      <FormLabel color="brand.dark">Sell In</FormLabel>
                      <div style={{ position: "relative" }}>
                        <CurrencyInputAlternateMM
                          key={`sellIn-${buttonClickClear}-${pesquisa.id}`}
                          prefix="  R$ "
                          input={sellIn}
                          setSellIn={setSellIn}
                          setSellInActive={true}
                          inputRef={sellInRef}
                          onBlur={onBlurSellIn}
                          disabled={disableAllInputs}
                          customizedHandleChange={(e) => {
                            sellInRef?.current?.focus();
                            setSellIn("0,00");
                            if (e?.setValue && typeof e?.setValue == "function") e.setValue("0,00");
                          }}
                        />
                        {showOverlay?.sellIn && (
                          <InputNoInformationOverlay
                            setShowOverlay={setShowOverlay}
                            showOverlay={showOverlay}
                            name="sellIn"
                            inputRef={sellInRef}
                          />
                        )}
                      </div>
                    </FormControl>

                    <GridItem>
                      <FormControl>
                        <>
                          <FormLabel color="brand.dark">
                            <p className="flex md:w-[500px] w-[440px] h-[24px] ">
                              <span className="">Sell Out </span>
                              <p className="relative ml-2 ">
                                <Info text="Valores referentes à 5 sacos à vista para retira" />
                              </p>
                            </p>
                          </FormLabel>
                          <CurrencyInputAlternateMM
                            prefix="  R$ "
                            input={sellOut}
                            onChangeSellOut={(event) =>
                              handleChangeGeneric({
                                event,
                                key: "sellOut",
                                setState: setSellOut,
                              })
                            }
                            onChangeActive={true}
                            inputRef={sellOutRef}
                            onBlur={onBlurSellOut}
                            disabled={disableAllInputs}
                            customizedHandleChange={(e) => {
                              sellOutRef?.current?.focus();
                              setSellOut(0);
                              if (e?.setValue && typeof e?.setValue == "function") e.setValue("");
                            }}
                          />
                        </>
                      </FormControl>
                    </GridItem>

                    <FormControl>
                      <FormLabel color="brand.dark">Markup</FormLabel>
                      <Input
                        bg="white"
                        value={showOverlay?.sellIn && markup === 0 ? "Sem informação" : markup + "%"}
                        className="cursor-not-allowed bg-gray-300"
                        disabled
                      />
                    </FormControl>
                  </>
                )}

                {trilhaCliente === "IND" && (
                  <>
                    <FormControl>
                      <FormLabel color="brand.dark">Preço FOB</FormLabel>
                      <div style={{ position: "relative" }}>
                        <Input
                          bg="white"
                          value={`R$${precoFOB}`}
                          onChange={(event) =>
                            handleChangeGeneric({
                              event,
                              key: "precoFOB",
                              setState: setPrecoFOB,
                            })
                          }
                          className=" bg-gray-300"
                          onBlur={onBlurPrecoFOB}
                          ref={precoFobRef}
                          disabled={disableAllInputs}
                        />
                        {showOverlay?.precoFOB && (
                          <InputNoInformationOverlay
                            setShowOverlay={setShowOverlay}
                            showOverlay={showOverlay}
                            name="precoFOB"
                            inputRef={precoFobRef}
                          />
                        )}
                      </div>
                    </FormControl>

                    <FormControl>
                      <FormLabel color="brand.dark">Preço CIF</FormLabel>
                      <div style={{ position: "relative" }}>
                        <Input
                          bg="white"
                          value={`R$${precoCIF}`}
                          onChange={(event) =>
                            handleChangeGeneric({
                              event,
                              key: "precoCIF",
                              setState: setPrecoCIF,
                            })
                          }
                          className=" bg-gray-300"
                          onBlur={onBlurPrecoCIF}
                          disabled={disableAllInputs}
                          ref={precoCifRef}
                        />
                        {showOverlay?.precoCIF && (
                          <InputNoInformationOverlay
                            setShowOverlay={setShowOverlay}
                            showOverlay={showOverlay}
                            name="precoCIF"
                            inputRef={precoCifRef}
                          />
                        )}
                      </div>
                    </FormControl>
                  </>
                )}
              </Grid>
            </Box>
          </Flex>
        </div>
      </div>
    );
  }
);
const InputNoInformationOverlay = ({ name, showOverlay, setShowOverlay, inputRef, inputText = "Sem Informação" }) => {
  const handleFocus = () => {
    inputRef?.current?.focus();
    setShowOverlay({
      ...showOverlay,
      [name]: false,
    });
  };

  return (
    <div
      onClick={handleFocus}
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "white",
        display: "flex",
        justifyContent: "start",
        alignItems: "center",
        border: "1px solid #cbd5e0",
        paddingLeft: "15px  ",
        borderRadius: "5px",
        cursor: "pointer",
        zIndex: 1,
      }}
    >
      { inputText }
    </div>
  );
};

PriceSearchItem.displayName = "PriceSearchItem";

PriceSearchItem.propTypes = {
  pesquisa: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    clienteId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    ultimaAtualizacao: PropTypes.string,
    ultimaAtualizacaoEN: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    dadosGerais: PropTypes.shape({
      incoterms: PropTypes.number,
      grupoProduto: PropTypes.shape({
        nome: PropTypes.string,
      }),
      produto: PropTypes.shape({
        nome: PropTypes.string,
      }),
      embalagemCapacidade: PropTypes.shape({
        nome: PropTypes.string,
      }),
      canal: PropTypes.shape({
        nome: PropTypes.string,
      }),
      pesquisaCentroOrigem: PropTypes.shape({
        nome: PropTypes.string,
      }),
      player: PropTypes.shape({
        nome: PropTypes.string,
      }),
    }).isRequired,
    infosAdicionais: PropTypes.shape({
      volume: PropTypes.number.isRequired,
      sellIn: PropTypes.number.isRequired,
      sellOut: PropTypes.number.isRequired,
      precoFOB: PropTypes.number.isRequired,
      precoCIF: PropTypes.number.isRequired,
      medida: PropTypes.shape({
        nome: PropTypes.string,
      }),
    }).isRequired,
  }).isRequired,
  handleDelete: PropTypes.func.isRequired,
  trilhaCliente: PropTypes.oneOf(["TAC", "IND"]).isRequired,
  setModalSettings: PropTypes.func.isRequired,
  setModalResponseSettings: PropTypes.func.isRequired,
  lastSearchDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  updateHook: PropTypes.func.isRequired,
  diasMostrarDataAtualizacao: PropTypes.number.isRequired,
};

InputNoInformationOverlay.propTypes = {
  name: PropTypes.string.isRequired,
  showOverlay: PropTypes.object.isRequired,
  setShowOverlay: PropTypes.func.isRequired,
  inputRef: PropTypes.shape({
    current: PropTypes.instanceOf(Element),
  }).isRequired,
};

export default PriceSearchItem;
